import { FormikProps, useFormikContext, withFormik } from 'formik'
import React, { FormEventHandler } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { DepositFormWarningBox } from '@pages/Traders-Room/DepositFormWarningBox'
import {
  validateWalletDepositForm,
  walletDepositInitialState,
} from '@pages/Traders-Room/Wallets/WalletDeposit/WalletDepositForm/WalletDepositForm'
import {
  GetDepositCurrencyLimits,
  useGetDepositCurrencyLimits,
} from '@pages/Traders-Room/Wallets/WalletDeposit/WalletDepositForm/hooks/useGetDepositCurrencyLimits'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { PopoverInput } from '@ui/components/Popover'
import { CaretIcon } from '@ui/icons/Caret.icon'

import { snakeToCamel } from '@utils/format.utils'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { PaymentProvider } from '@model/PaymentProviderDto'
import { getCryptoPaymentProviderFlags } from '@model/PaymentProviderId'
import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'
import { WalletDto, WalletTypeEnum } from '@model/WalletDto'

import { createFormField } from '@global/formField/FormField'

import { FormatMoney } from '@hooks/useFormatNumber'
import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { FieldWrapper } from '@shared/components/FieldWrapper'
import { WalletDepositPaymentProviderModal } from '@shared/modals/WalletDepositPaymentProviderModal'

import { useDepositOptions } from '../hooks/useDepositOptions'
import { FirstTimeDepositPSPFields } from './FirstTimeDepositPSPFields'

import styles from './FirstTimeDepositForm.module.scss'

interface OuterProps {
  wallets: WalletDto[]
  skipButtonLink: string
  onSubmit: (values: WalletDepositFormValues) => void
  formatMoney: FormatMoney
  getDepositCurrencyLimits: GetDepositCurrencyLimits
}

const FormField = createFormField<WalletDepositFormValues>()

const FirstTimeDepositFormUI: React.FC<FormikProps<WalletDepositFormValues> & OuterProps> = (
  props
) => {
  useOnLocaleSwitch(() => props.validateForm())
  const { wallets, skipButtonLink, formatMoney } = props
  const { handleSubmit, values, resetForm, setValues } = useFormikContext<WalletDepositFormValues>()
  const { isOrbitalCryptoProvider } = getCryptoPaymentProviderFlags(values.paymentProvider.id)
  const { t } = useTranslation()
  const { account } = useAccountReadContext()

  const DISPLAY_BY_RESTRICTIONS = true

  const { radioOptions, allOptions } = useDepositOptions(
    wallets,
    WalletRestrictions.DEPOSIT,
    DISPLAY_BY_RESTRICTIONS
  )

  const { depositCurrencyLimits } = useGetDepositCurrencyLimits({
    wallet: values.wallet,
    paymentProvider: values.paymentProvider,
  })

  const isWalletFieldShown =
    wallets.length > 1 ||
    (wallets.length === 1 && isRestricted(WalletRestrictions.DEPOSIT, wallets[0].restrictions))

  const onSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()

    props.validateForm()
    handleSubmit()
  }

  const handleWallet = (wallet: WalletDto) => {
    resetForm()
    setValues({
      ...walletDepositInitialState(),
      wallet,
      walletType: values.walletType,
    })
  }

  const handlePaymentProvider = (paymentProvider: PaymentProvider) => {
    resetForm()
    setValues({
      ...values,
      paymentProvider,
      fields: Object.fromEntries(
        paymentProvider.parameters.form
          .filter((f) => !!f.field)
          .map((field) => [
            snakeToCamel(field.field),
            Array.isArray(field.value) ? field.value[0]?.value ?? '' : String(field.value ?? ''),
          ])
      ),
    })
    paymentProviderModal.close()
  }

  const paymentProviderModal = useModal(WalletDepositPaymentProviderModal, {
    onSelectOption: handlePaymentProvider,
    wallet: values.wallet,
  })

  return (
    <Form className={styles.form} onSubmit={onSubmit} autoComplete='off'>
      {isOrbitalCryptoProvider && (
        <DepositFormWarningBox className={styles.formItem}>
          {t(
            'Wallet.The wallet address and exchange rate will expire within 1 hour, please make your payment within this time.'
          )}
        </DepositFormWarningBox>
      )}
      <FieldWrapper>
        {isWalletFieldShown && (
          <PopoverInput
            name='wallet.name'
            label={
              values.wallet?.id
                ? t('Wallet.Wallet Currency Name', {
                    walletCurrencyId: values.wallet.currency.id,
                    walletName: values.wallet.name,
                  })
                : t('Wallet.Deposit To Wallet')
            }
            value={values.wallet?.id}
            hint={
              values.wallet?.id
                ? `${t('Wallet.Balance')} ${formatMoney(
                    values.wallet.balance,
                    values.wallet.currency.id
                  )}`
                : undefined
            }
            required
            options={radioOptions
              .filter((opt) => opt.value != null && opt.value !== undefined)
              .map((opt) => ({
                name: opt.label ?? '',
                id: opt.value ?? '',
                disabled: opt.disabled,
              }))}
            onChange={(option) => {
              const selectedWallet = allOptions.find((o) => o.id === option.id)
              if (!selectedWallet) return
              handleWallet(selectedWallet)
            }}
          />
        )}
        <FormField
          name='paymentProvider.name'
          value={values.paymentProvider?.currency?.description}
          label={t('Sign up.Payment method')}
          rightIcon={CaretIcon.Down}
          disabled={!values.wallet?.id}
          readOnly
          onClick={paymentProviderModal.open}
          key={values.paymentProvider?.id ? 'paymentProvider' : 'no-paymentProvider'}
        />
        <FirstTimeDepositPSPFields
          minAmount={depositCurrencyLimits?.minAmount}
          showAmountPresets={isAccountActivatedStatus(account)}
          skipButtonLink={skipButtonLink}
        />
      </FieldWrapper>
    </Form>
  )
}

export const FirstTimeDepositForm = withFormik<OuterProps, WalletDepositFormValues>({
  mapPropsToValues: ({ wallets }) => {
    const filteredWallets = wallets?.filter(
      (wallet) => !isRestricted(WalletRestrictions.DEPOSIT, wallet?.restrictions)
    )
    const wallet = filteredWallets?.find((wallet) => wallet.isDefault) ?? filteredWallets?.[0]
    return walletDepositInitialState({ wallet, walletType: WalletTypeEnum.PAYMENT_AGENT })
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: validateWalletDepositForm,
  enableReinitialize: true,
  validateOnMount: true,
})(FirstTimeDepositFormUI)
